import React, { useContext, useReducer } from 'react';
import {
  InputField,
  SelectField,
  CheckboxField,
  useRepeatedSection
} from '@trp-beacon-ui-kit/react-controls';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import { states } from '../FONASUCustomerInformation/util';
import styles from './styles.css';
import { FlowContext } from '../../core/flow-engine';
import {
  stateValidation,
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  cityValidation,
  zipCodeValidation
} from '../../flows/shared/validations';
import {
  generateGeneralTestID,
  generateJointOrCoTrustTestID
} from '../../generateAutomationTestIDs';

export const RenderBox = ({ additionalCustomers }) => {
  const {
    formProps: {
      jointOwnerFirstName,
      jointOwnerLastName,
      jointOwnerSameAddress,
      jointOwnerResidentialStreet1,
      jointOwnerResidentialStreet2,
      jointOwnerResidentialZip,
      jointOwnerResidentialCity,
      jointOwnerResidentialState
    }
  } = useContext(FlowContext);

  // this reducer manages the varying checked/unchecked
  // states of the "same address" checkboxes
  const reducer = (state, idx) => {
    const prevButton = state[idx];
    const nextButtons = [...state];
    nextButtons[idx] = !prevButton;
    return nextButtons;
  };

  const [sameAddressChecked, toggleSameAddressChecked] = useReducer(
    reducer,
    additionalCustomers.map((item, idx) => {
      const sameAddressUndefined =
        typeof additionalCustomers[idx]?.sameAddress === 'undefined';
      return sameAddressUndefined
        ? true
        : additionalCustomers[idx]?.sameAddress;
    })
  );

  const defaults = {
    people: additionalCustomers?.map(person => ({
      [jointOwnerFirstName.name]: person?.name?.firstName,
      [jointOwnerLastName.name]: person?.name?.lastName,
      [jointOwnerSameAddress.name]: person?.sameAddress,
      [jointOwnerResidentialStreet1.name]: person?.residentialAddress?.street1,
      [jointOwnerResidentialStreet2.name]: person?.residentialAddress?.street2,
      [jointOwnerResidentialZip.name]: person?.residentialAddress?.zip,
      [jointOwnerResidentialCity.name]: person?.residentialAddress?.city,
      [jointOwnerResidentialState.name]: person?.residentialAddress?.state
    }))
  };

  const { sections } = useRepeatedSection('people', defaults);

  return (
    <div className="sections">
      {sections.map(({ key, getDefault, makeName }, idx) => {
        const { firstName = '', lastName = '' } =
          additionalCustomers[idx]?.name || {};
        return (
          <div key={key} className="section">
            <div className="review-section">
              <CheckboxField
                title={`Use same contact information for ${firstName} ${lastName}`}
                {...jointOwnerSameAddress}
                name={makeName(jointOwnerSameAddress.name)}
                data-name={generateJointOrCoTrustTestID(
                  'JointOwnerCI',
                  'JointSameAddressCheckbox',
                  idx
                )}
                defaultChecked={sameAddressChecked[idx]}
                onClick={() => toggleSameAddressChecked(idx)}
              />

              {!sameAddressChecked[idx] && (
                <div className="address-wrapper">
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Residential / Legal Address Line 1"
                        {...jointOwnerResidentialStreet1}
                        name={makeName(jointOwnerResidentialStreet1.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialStreet1.name
                        )}
                        validation={[
                          addressLine1Validation,
                          poBoxLine1Validation
                        ]}
                        maxLength="35"
                        className="blocked_data"
                        data-name={generateJointOrCoTrustTestID(
                          'JointOwnerCI',
                          'JointOwnerResidentialStreetOne',
                          idx
                        )}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Residential / Legal Address Line 2"
                        {...jointOwnerResidentialStreet2}
                        name={makeName(jointOwnerResidentialStreet2.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialStreet2.name
                        )}
                        validation={[
                          addressLine2Validation,
                          poBoxLine2Validation
                        ]}
                        maxLength="35"
                        className="blocked_data"
                        data-name={generateJointOrCoTrustTestID(
                          'JointOwnerCI',
                          'JointOwnerResidentialStreetTwo',
                          idx
                        )}
                      />
                    </div>
                  </div>
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="City"
                        {...jointOwnerResidentialCity}
                        name={makeName(jointOwnerResidentialCity.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialCity.name
                        )}
                        validation={cityValidation}
                        className="blocked_data"
                        data-name={generateJointOrCoTrustTestID(
                          'JointOwnerCI',
                          'JointOwnerResidentialCity',
                          idx
                        )}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Zip Code"
                        {...jointOwnerResidentialZip}
                        name={makeName(jointOwnerResidentialZip.name)}
                        defaultValue={getDefault(jointOwnerResidentialZip.name)}
                        validation={zipCodeValidation}
                        maxLength="5"
                        className="blocked_data"
                        data-name={generateJointOrCoTrustTestID(
                          'JointOwnerCI',
                          'JointOwnerResidentialZip',
                          idx
                        )}
                      />
                    </div>
                    <div className="qv-col">
                      <SelectField
                        title="State"
                        {...jointOwnerResidentialState}
                        name={makeName(jointOwnerResidentialState.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialState.name
                        )}
                        validation={stateValidation}
                        data-name={generateJointOrCoTrustTestID(
                          'JointOwnerCI',
                          'JointOwnerResidentialState',
                          idx
                        )}>
                        <option hidden value="">
                          State
                        </option>
                        {states.map(item => (
                          <option
                            key={item}
                            value={item}
                            data-name={generateGeneralTestID(
                              'JointOwnerPI',
                              `SelectResidentialState_${item}`
                            )}>
                            {item}
                          </option>
                        ))}
                      </SelectField>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      <style jsx>{styles}</style>
    </div>
  );
};

const JointOwnerCISection = () => {
  const {
    appState: {
      store: { additionalCustomers }
    }
  } = useContext(FlowContext);

  return (
    <>
      <div className="section-header">
        <SectionHeading text="Joint Owner(s)" />
      </div>
      <RenderBox additionalCustomers={additionalCustomers} />
      <style jsx>{styles}</style>
    </>
  );
};

export default JointOwnerCISection;
